import {
  canExecuteOtherUserNegotiatedRFQ,
  Dialog,
  Text,
  useCallbackRef,
  useDisclosure,
  type Quote,
} from '@talos/kyoko';

import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useUser } from '@src/hooks/useUser';
import { useRef } from 'react';

type RFQConfirmationAction = 'trade' | 'cancel';

function RFQConfirmationContent({
  action,
  quote,
}: {
  action: RFQConfirmationAction;
  quote: Pick<Quote, 'User'> | undefined;
}) {
  return (
    <Text maxWidth="300px">
      You are about to {action} a negotiated RFQ belonging to {quote?.User}. Are you sure you want to proceed?
    </Text>
  );
}

export function shouldConfirmOtherUserNegotiatedRFQAction({
  quote,
  currentUserName,
  enableExecutingOtherUserNegotiatedRFQ,
}: {
  quote: Pick<Quote, 'RFQTradingProtocol' | 'User'> | undefined;
  currentUserName: string | undefined;
  enableExecutingOtherUserNegotiatedRFQ: boolean;
}): boolean {
  return (
    canExecuteOtherUserNegotiatedRFQ({
      rfqTradingProtocol: quote?.RFQTradingProtocol,
      enableExecutingOtherUserNegotiatedRFQ,
    }) &&
    quote?.User != null &&
    currentUserName != null &&
    quote.User !== currentUserName
  );
}

function useRFQActionConfirmation<TArgs extends readonly unknown[]>({
  quote,
  execute,
  action,
  title,
  confirmLabel,
  usePortal = true,
}: {
  quote: Pick<Quote, 'RFQTradingProtocol' | 'User'> | undefined;
  execute: (...args: TArgs) => void | Promise<void>;
  action: RFQConfirmationAction;
  title: string;
  confirmLabel: string;
  usePortal?: boolean;
}) {
  const dialog = useDisclosure();

  // We're "holding" the call to execute here while the user is deciding to confirm or cancel
  // The args are held in this ref, and if the user confirms, we'll call execute with them
  const pendingArgsRef = useRef<TArgs>();
  const user = useUser();
  const { enableExecutingOtherUserNegotiatedRFQ } = useFeatureFlag();

  const handleConfirm = useCallbackRef(() => {
    const pendingArgs = pendingArgsRef.current;
    pendingArgsRef.current = undefined;
    dialog.close();

    if (pendingArgs != null) {
      void execute(...pendingArgs);
    }
  });

  const executeWithConfirmation = useCallbackRef((...args: TArgs) => {
    if (
      shouldConfirmOtherUserNegotiatedRFQAction({
        quote,
        currentUserName: user.Name,
        enableExecutingOtherUserNegotiatedRFQ,
      })
    ) {
      pendingArgsRef.current = args;
      dialog.open();
      return;
    }

    return execute(...args);
  });

  return {
    execute: executeWithConfirmation,
    dialog: (
      <Dialog
        {...dialog}
        usePortal={usePortal}
        title={title}
        confirmLabel={confirmLabel}
        cancelLabel="Cancel"
        onConfirm={handleConfirm}
        stretchButtons
      >
        <RFQConfirmationContent action={action} quote={quote} />
      </Dialog>
    ),
  };
}

export function useRFQTradingConfirmation<TArgs extends unknown[]>({
  quote,
  execute,
  usePortal = true,
}: {
  quote: Pick<Quote, 'RFQTradingProtocol' | 'User'> | undefined;
  execute: (...args: TArgs) => void | Promise<void>;
  usePortal?: boolean;
}) {
  return useRFQActionConfirmation({
    quote,
    execute,
    action: 'trade',
    title: "Trading Another User's RFQ",
    confirmLabel: 'Confirm',
    usePortal,
  });
}

export function useRFQCancellationConfirmation<TArgs extends unknown[]>({
  quote,
  execute,
  usePortal = true,
}: {
  quote: Pick<Quote, 'RFQTradingProtocol' | 'User'> | undefined;
  execute: (...args: TArgs) => void | Promise<void>;
  usePortal?: boolean;
}) {
  return useRFQActionConfirmation({
    quote,
    execute,
    action: 'cancel',
    title: "Canceling Another User's RFQ",
    confirmLabel: 'Confirm',
    usePortal,
  });
}
